import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Control, Controller, useForm } from 'react-hook-form';
import PatientPicker from '@components/Patient/PatientPicker';
import { EntitlementGate } from '@features/entitlements';
import { useInvalidatePatientCache } from '@features/patients/hooks';
import {
  useAccessToken,
  useGetProviderBillingInfoQuery,
  useSubjectLanguageVariants,
  useUpdateProviderBillingInfoMutation,
} from '@features/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';
import { z } from 'zod';

import type { Patient, ProviderBillingInfo } from '../../../types/types';
import { useDeleteV2ProviderLabsBundlesByBundleIdMutation } from '../store/api';
import { useAvailableLabTests } from '../store/hooks';
import type { CreateLabOrderRequest } from '../types';
import { labOrderSchema } from '../utils/validation';

import { RequiredInfoFields } from './RequiredInfoFields';
import { SaveBundleDialog } from './SaveBundleDialog';

export interface LabOrderFormProps {
  defaultValues?: Partial<CreateLabOrderRequest>;
  patientId?: string;
  showPatientPicker?: boolean;
  onSubmit: (data: CreateLabOrderRequest) => void | Promise<void>;
  onChange?: (data: Partial<CreateLabOrderRequest>) => void;
  isLoading?: boolean;
  formRef?: React.RefObject<HTMLFormElement | null>;
  onDirtyChange?: (isDirty: boolean) => void;
  pendingRecommendations?: Partial<CreateLabOrderRequest> | null;
  shouldApplyRecommendations?: boolean;
  onRecommendationsApplied?: () => void;
  autoApplyRecommendations?: Partial<CreateLabOrderRequest> | false;
  onPatientSelect?: (patient: Patient | undefined) => void;
}

export const LabOrderForm: React.FC<LabOrderFormProps> = ({
  defaultValues,
  patientId,
  showPatientPicker = true,
  onSubmit,
  onChange,
  isLoading = false,
  formRef,
  onDirtyChange,
  pendingRecommendations,
  shouldApplyRecommendations,
  onRecommendationsApplied,
  autoApplyRecommendations,
  onPatientSelect,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken } = useAccessToken();
  const invalidatePatientCache = useInvalidatePatientCache();
  const { singularCap: subjectLanguageCap } = useSubjectLanguageVariants();
  const { patient: patientMetrics, labOrder } = useEventTracking();
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>();
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasAutoApplied, setHasAutoApplied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveBundleDialogOpen, setSaveBundleDialogOpen] = useState(false);
  const [deleteBundle] = useDeleteV2ProviderLabsBundlesByBundleIdMutation();

  const {
    labTests,
    labBundles,
    isLoading: isLoadingTests,
    error: testsError,
  } = useAvailableLabTests();

  // Provider billing info
  const { data: providerBillingData, isLoading: isLoadingProvider } =
    useGetProviderBillingInfoQuery(undefined);
  const [updateProviderBilling] = useUpdateProviderBillingInfoMutation();

  const labTestsMap = new Map(labTests.map(test => [test.test_id, test]));
  const labTestNames = labTests.map(test => test.name);

  // Type for autocomplete options
  type AutocompleteOption =
    | {
        label: string;
        category: string;
        type: 'bundle';
        data: (typeof labBundles)[0];
      }
    | {
        label: string;
        category: string;
        type: 'test';
        data: (typeof labTests)[0];
      };

  // Create unified options array with categories
  const autocompleteOptions: AutocompleteOption[] = [
    // Bundles first
    ...labBundles.map(bundle => ({
      label: bundle.name,
      category: 'Test Bundles',
      type: 'bundle' as const,
      data: bundle,
    })),
    // Individual tests
    ...labTests.map(test => ({
      label: test.name,
      category: 'Individual Tests',
      type: 'test' as const,
      data: test,
    })),
  ];

  // Extended form type to include required info fields
  type ExtendedLabOrderForm = CreateLabOrderRequest & {
    // Provider fields
    fax_number?: string;
    npi_number?: string;
    communications_name?: string;
    signing_name?: string;
    // Patient fields
    patient_dob?: string;
  };

  // Determine missing fields
  const missingFields = useMemo(() => {
    const providerInfo = providerBillingData?.provider_billing_info || {};

    const missingProvider: Array<
      keyof Pick<
        ProviderBillingInfo,
        'fax_number' | 'npi_number' | 'communications_name' | 'signing_name'
      >
    > = [];
    const missingPatient: Array<'dob'> = [];

    // Check provider fields. A clinician without an NPI who bills under a
    // supervising provider orders labs under the supervisor's NPI (GDN-1926),
    // so their own NPI isn't required.
    if (!providerInfo.fax_number) missingProvider.push('fax_number');
    if (!providerInfo.npi_number && !providerInfo.bill_under_user_id)
      missingProvider.push('npi_number');
    if (!providerInfo.communications_name) missingProvider.push('communications_name');
    if (!providerInfo.signing_name) missingProvider.push('signing_name');

    // Check patient fields (email is handled by PatientSendPanel in Step 3)
    if (selectedPatient && !selectedPatient.dob) missingPatient.push('dob');

    return { provider: missingProvider, patient: missingPatient };
  }, [providerBillingData, selectedPatient]);

  // Create dynamic validation schema based on missing fields
  const dynamicRequiredSchema = useMemo(() => {
    let schema = labOrderSchema;

    // Add required validation for missing provider fields
    if (missingFields.provider.includes('fax_number')) {
      schema = schema.extend({
        fax_number: z.string().min(1, 'Fax number is required'),
      });
    }
    if (missingFields.provider.includes('npi_number')) {
      schema = schema.extend({
        npi_number: z.string().regex(/^\d{10}$/, 'NPI must be exactly 10 digits'),
      });
    }
    if (missingFields.provider.includes('communications_name')) {
      schema = schema.extend({
        communications_name: z.string().min(1, 'Communications name is required'),
      });
    }
    if (missingFields.provider.includes('signing_name')) {
      schema = schema.extend({
        signing_name: z.string().min(1, 'Signing name is required'),
      });
    }

    // Add required validation for missing patient fields
    if (missingFields.patient.includes('dob')) {
      schema = schema.extend({
        patient_dob: z.string().min(1, 'Date of birth is required'),
      });
    }

    return schema;
  }, [missingFields]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ExtendedLabOrderForm>({
    resolver: zodResolver(dynamicRequiredSchema),
    defaultValues: {
      patient_id: patientId || defaultValues?.patient_id || '',
      lab_tests: defaultValues?.lab_tests || [],
      icd_10_codes: defaultValues?.icd_10_codes || ['Z79.899'],
      special_instructions: defaultValues?.special_instructions || '',
      sign_with_name: defaultValues?.sign_with_name || false,
      // Extended fields for provider/patient info
      fax_number: '',
      npi_number: '',
      communications_name: '',
      signing_name: '',
      patient_dob: '',
    },
  });

  // Watch specific fields for recommendation-related changes
  const watchedLabTests = watch('lab_tests');
  const watchedIcdCodes = watch('icd_10_codes');
  const watchedSpecialInstructions = watch('special_instructions');

  // Track if recommendation-related fields have been modified
  const [isRecommendationFieldsDirty, setIsRecommendationFieldsDirty] = useState(false);

  // Check if recommendation fields are dirty (only if recommendations haven't been applied)
  useEffect(() => {
    if (hasAutoApplied) {
      // Once recommendations are applied, don't mark as dirty again
      setIsRecommendationFieldsDirty(false);
      return;
    }

    const defaultLabTests = defaultValues?.lab_tests || [];
    const defaultIcdCodes = defaultValues?.icd_10_codes || ['Z79.899'];
    const defaultInstructions = defaultValues?.special_instructions || '';

    const labTestsChanged =
      JSON.stringify(watchedLabTests || []) !== JSON.stringify(defaultLabTests);
    const icdCodesChanged =
      JSON.stringify(watchedIcdCodes || []) !== JSON.stringify(defaultIcdCodes);
    const instructionsChanged = (watchedSpecialInstructions || '') !== defaultInstructions;

    setIsRecommendationFieldsDirty(labTestsChanged || icdCodesChanged || instructionsChanged);
  }, [watchedLabTests, watchedIcdCodes, watchedSpecialInstructions, defaultValues, hasAutoApplied]);

  // Watch form and notify parent of changes
  useEffect(() => {
    if (onChange) {
      const subscription = watch(value => {
        onChange(value);
      });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [watch, onChange]);

  // Update patient_id when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      setValue('patient_id', selectedPatient.patient_id);
      onPatientSelect?.(selectedPatient);
    }
  }, [selectedPatient, setValue, onPatientSelect]);

  // Fetch patient data when patientId is provided
  useEffect(() => {
    const fetchPatientData = async () => {
      if (patientId && !showPatientPicker) {
        setIsLoadingPatientData(true);
        try {
          const accessToken = await getAccessToken();
          const response = await APIService.makeAPIGetRequest({
            requestString: `/patient/get?patient_id=${patientId}&origin=LabOrderForm`,
            accessToken,
          });

          if (response.ok) {
            const data = (await response.value) as { patient: Patient };
            setSelectedPatient(data.patient);
            // onPatientSelect will be called by the effect watching selectedPatient
          } else {
            console.error('Failed to fetch patient data');
          }
        } catch (error) {
          console.error('Error fetching patient:', error);
        } finally {
          setIsLoadingPatientData(false);
        }
      }
    };

    fetchPatientData();
  }, [patientId, showPatientPicker, getAccessToken]);

  // Track dirty state for recommendation fields only
  useEffect(() => {
    onDirtyChange?.(isRecommendationFieldsDirty);
  }, [isRecommendationFieldsDirty, onDirtyChange]);

  // Auto-apply recommendations on mount if provided
  useEffect(() => {
    if (autoApplyRecommendations && !isRecommendationFieldsDirty && !hasAutoApplied) {
      if (autoApplyRecommendations.lab_tests) {
        setValue('lab_tests', autoApplyRecommendations.lab_tests);
      }
      if (autoApplyRecommendations.icd_10_codes) {
        setValue('icd_10_codes', autoApplyRecommendations.icd_10_codes);
      }
      if (autoApplyRecommendations.special_instructions !== undefined) {
        setValue('special_instructions', autoApplyRecommendations.special_instructions || '');
      }
      setHasAutoApplied(true);
    }
  }, [autoApplyRecommendations, isRecommendationFieldsDirty, setValue, hasAutoApplied]);

  // Handle applying pending recommendations
  const handleApplyRecommendations = useCallback(() => {
    if (pendingRecommendations) {
      // Apply all recommendation fields, including empty/null values to match AI exactly
      setValue('lab_tests', pendingRecommendations.lab_tests || [], { shouldDirty: true });
      setValue('icd_10_codes', pendingRecommendations.icd_10_codes || [], { shouldDirty: true });
      setValue('special_instructions', pendingRecommendations.special_instructions || '', {
        shouldDirty: true,
      });
      // Mark that recommendations have been applied
      setHasAutoApplied(true);
    }
  }, [pendingRecommendations, setValue]);

  // Watch for trigger to apply recommendations
  useEffect(() => {
    if (shouldApplyRecommendations && pendingRecommendations) {
      handleApplyRecommendations();
      onRecommendationsApplied?.();
    }
  }, [
    shouldApplyRecommendations,
    pendingRecommendations,
    handleApplyRecommendations,
    onRecommendationsApplied,
  ]);

  if (isLoadingTests) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 3 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading available lab tests...
        </Typography>
      </Box>
    );
  }

  if (testsError) {
    return (
      <Alert severity="error">
        Failed to load available lab tests. Please refresh the page and try again.
      </Alert>
    );
  }

  // Handle form submission with validation and API calls
  const handleFormSubmit = async (data: ExtendedLabOrderForm) => {
    setIsSubmitting(true);

    try {
      // Prepare update promises to run in parallel
      const updatePromises: Promise<void>[] = [];

      // Provider updates
      const providerUpdates: Partial<ProviderBillingInfo> = {};
      let hasProviderUpdates = false;

      if (data.fax_number) {
        providerUpdates.fax_number = data.fax_number;
        hasProviderUpdates = true;
      }
      if (data.npi_number) {
        providerUpdates.npi_number = data.npi_number;
        hasProviderUpdates = true;
      }
      if (data.communications_name) {
        providerUpdates.communications_name = data.communications_name;
        hasProviderUpdates = true;
      }
      if (data.signing_name) {
        providerUpdates.signing_name = data.signing_name;
        hasProviderUpdates = true;
      }

      if (hasProviderUpdates) {
        updatePromises.push(
          updateProviderBilling({ updated_fields: providerUpdates })
            .unwrap()
            .then(() => undefined),
        );
      }

      // Patient updates (email is handled by PatientSendPanel in Step 3)
      const patientUpdates: Partial<Patient> = {};
      let hasPatientUpdates = false;

      if (data.patient_dob && selectedPatient) {
        patientUpdates.dob = data.patient_dob;
        hasPatientUpdates = true;
      }

      if (hasPatientUpdates && selectedPatient) {
        const updatePatientPromise = async () => {
          const accessToken = await getAccessToken();
          const response = await APIService.makeAPIPostRequest({
            requestString: '/patient/update',
            accessToken,
            body: {
              patient_id: selectedPatient.patient_id,
              updated_fields: patientUpdates,
            },
          });

          if (!response.ok) {
            throw new Error(response.error?.message || 'Failed to update patient information');
          }

          invalidatePatientCache(selectedPatient.patient_id);

          // Track patient update
          const fieldsUpdated = Object.keys(patientUpdates);
          patientMetrics.updated(selectedPatient.patient_id, {
            source: 'lab_order',
            fields_updated: fieldsUpdated,
          });

          // Update local patient state
          setSelectedPatient(prev => (prev ? { ...prev, ...patientUpdates } : prev));
        };

        updatePromises.push(updatePatientPromise());
      }

      // Execute all updates in parallel
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }

      // Prepare lab order data (exclude the extra fields)
      const labOrderData: CreateLabOrderRequest = {
        patient_id: data.patient_id,
        lab_tests: data.lab_tests,
        icd_10_codes: data.icd_10_codes,
        special_instructions: data.special_instructions,
        sign_with_name: data.sign_with_name,
      };

      // Call parent onSubmit
      await onSubmit(labOrderData);
    } catch (error) {
      console.error('Error submitting lab order form:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to process lab order', {
        variant: 'error',
      });
      // Re-throw to let parent handle it too
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EntitlementGate capabilityName="lab_order_creation" source="lab_order_form">
      <Box component="form" ref={formRef} onSubmit={handleSubmit(handleFormSubmit)} sx={{ p: 2 }}>
        <Stack spacing={3}>
          {/* Patient Selection */}
          {showPatientPicker && (
            <FormControl fullWidth error={!!errors.patient_id}>
              <Typography variant="subtitle1" gutterBottom>
                Patient *
              </Typography>
              <PatientPicker
                onPatientSelect={patient => {
                  setSelectedPatient(patient);
                  onPatientSelect?.(patient);
                }}
                initialPatient={selectedPatient}
              />
              {errors.patient_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.patient_id.message}
                </Typography>
              )}
            </FormControl>
          )}

          {/* Required Info Fields - Only show if there are missing fields and patient is selected */}
          {selectedPatient &&
            (missingFields.provider.length > 0 || missingFields.patient.length > 0) && (
              <>
                <RequiredInfoFields
                  providerData={providerBillingData?.provider_billing_info}
                  patientData={selectedPatient}
                  missingFields={missingFields}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  control={control as Control<any>}
                  errors={errors}
                  isLoadingProvider={isLoadingProvider}
                  isLoadingPatient={isLoadingPatientData}
                />
                <Divider />
              </>
            )}

          {/* Optional: Keep bundle cards for visual reference - can be removed if dropdown is sufficient */}
          {/* {bundles.length > 0 && (
          <FormControl fullWidth>
            ... bundle cards code ...
          </FormControl>
        )} */}

          {/* Lab Tests Selection */}
          <FormControl fullWidth error={!!errors.lab_tests}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Lab Tests *
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Search and select tests to order, or type custom test names
                </Typography>
              </Box>
              {watchedLabTests && watchedLabTests.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => setSaveBundleDialogOpen(true)}
                  sx={{ mt: 0.5 }}
                >
                  Save as Bundle
                </Button>
              )}
            </Box>

            <Controller
              name="lab_tests"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Autocomplete
                    open={open}
                    onOpen={() => {
                      setOpen(true);
                    }}
                    onClose={() => {
                      setOpen(false);
                    }}
                    multiple
                    freeSolo
                    disableClearable
                    disableCloseOnSelect
                    options={autocompleteOptions}
                    groupBy={option =>
                      typeof option === 'string' ? 'Custom Tests' : option.category
                    }
                    getOptionLabel={option => {
                      // Handle string values (custom tests)
                      if (typeof option === 'string') return option;
                      return option.label;
                    }}
                    value={value || []}
                    onChange={(event, newValue, reason, details) => {
                      // Handle bundle selection
                      if (
                        details?.option &&
                        typeof details.option !== 'string' &&
                        details.option.type === 'bundle'
                      ) {
                        const bundle = details.option.data;

                        // Get test names - either directly from test_names or by mapping test_ids
                        let bundleTestNames: string[];
                        if (bundle.test_names && bundle.test_names.length > 0) {
                          // Use test_names directly if available
                          bundleTestNames = bundle.test_names;
                        } else if (bundle.test_ids && bundle.test_ids.length > 0) {
                          // Fall back to mapping test_ids to names
                          bundleTestNames = bundle.test_ids
                            .map(testId => labTestsMap.get(testId)?.name)
                            .filter(Boolean) as string[];
                        } else {
                          // Bundle has no tests defined
                          bundleTestNames = [];
                        }

                        // Replace all tests with bundle tests
                        onChange(bundleTestNames);

                        // Track bundle selection
                        labOrder.bundleSelected({
                          bundle_name: bundle.name,
                          test_count: bundleTestNames.length,
                        });

                        // Replace special instructions with bundle instructions
                        setValue('special_instructions', bundle.special_instructions);

                        // Close the dropdown after bundle selection
                        setOpen(false);
                      } else if (
                        reason === 'selectOption' &&
                        details?.option &&
                        typeof details.option !== 'string'
                      ) {
                        // Handle individual test selection with toggle behavior
                        const clickedTestName = details.option.label;
                        const currentValues = value || [];

                        if (currentValues.includes(clickedTestName)) {
                          // Remove the test if it's already selected
                          onChange(currentValues.filter(test => test !== clickedTestName));
                        } else {
                          // Add the test if it's not selected
                          onChange([...currentValues, clickedTestName]);

                          // Track test selection
                          labOrder.testSelected({
                            is_bundle: false,
                            total_tests: currentValues.length + 1,
                          });
                        }
                      } else {
                        // Handle other cases (removeOption, clear, etc.)
                        const processedValue = newValue.map(item => {
                          if (typeof item === 'string') return item;
                          return item.label;
                        });
                        onChange(processedValue);
                      }
                    }}
                    filterSelectedOptions={true}
                    renderValue={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        // tagValue contains strings since that's what we store in the form value
                        const optionString = typeof option === 'string' ? option : option.label;
                        const isCustom = !labTestNames.includes(optionString);
                        const { key, ...chipProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant={isCustom ? 'filled' : 'outlined'}
                            label={optionString}
                            size="small"
                            color={isCustom ? 'secondary' : 'primary'}
                            {...chipProps}
                          />
                        );
                      })
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Search bundles, lab tests, or add custom test..."
                        helperText="Select a bundle to replace all tests with a pre-configured set, or build your own selection. Press Enter to add custom tests."
                        error={!!errors.lab_tests}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key: _key, ...optionProps } =
                        props;

                      if (typeof option === 'string') {
                        // Custom test option
                        return (
                          <Box component="li" {...optionProps}>
                            {option}
                          </Box>
                        );
                      }

                      if (option.type === 'bundle') {
                        const bundle = option.data;

                        return (
                          <Box
                            component="li"
                            {...optionProps}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              borderLeft: '3px solid',
                              borderColor: bundle.is_custom ? 'secondary.main' : 'primary.main',
                              pl: 1.5,
                              pr: 1.5,
                              py: 1,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ fontWeight: "600" }}>
                                  {option.label}
                                </Typography>
                                {bundle.is_custom && (
                                  <Chip
                                    label="Custom"
                                    size="small"
                                    color="secondary"
                                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {bundle.is_custom && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={async e => {
                                      e.stopPropagation();
                                      if (confirm(`Delete custom bundle "${bundle.name}"?`)) {
                                        try {
                                          await deleteBundle({
                                            bundleId: bundle.bundle_id,
                                          }).unwrap();
                                          enqueueSnackbar('Bundle deleted successfully', {
                                            variant: 'success',
                                          });
                                        } catch (error) {
                                          console.error('Failed to delete bundle:', error);
                                          enqueueSnackbar('Failed to delete bundle', {
                                            variant: 'error',
                                          });
                                        }
                                      }
                                    }}
                                    sx={{ p: 0.5 }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        Includes:
                                      </Typography>
                                      {(() => {
                                        // Get test names - either directly or by mapping IDs
                                        const testNames =
                                          bundle.test_names && bundle.test_names.length > 0
                                            ? bundle.test_names
                                            : bundle.test_ids
                                              ? bundle.test_ids
                                                  .map(id => labTestsMap.get(id)?.name)
                                                  .filter(Boolean)
                                              : [];

                                        return testNames.map((testName, index) => (
                                          <Typography key={index} variant="caption" sx={{ display: "block" }}>
                                            • {testName}
                                          </Typography>
                                        ));
                                      })()}
                                    </Box>
                                  }
                                  placement="left"
                                  enterDelay={300}
                                  arrow
                                  slotProps={{
                                    popper: {
                                      sx: {
                                        pointerEvents: 'none',
                                      },
                                    },
                                  }}
                                >
                                  <Chip
                                    label={`${
                                      bundle.test_names?.length || bundle.test_ids?.length || 0
                                    } tests`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.75rem' }}
                                  />
                                </Tooltip>
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                display: 'block',
                                textAlign: 'left',
                                alignSelf: 'flex-start',
                              }}
                            >
                              {bundle.description}
                            </Typography>
                          </Box>
                        );
                      }

                      // Individual test option
                      const test = option.data;
                      const isSelected = (value || []).includes(option.label);

                      return (
                        <Box
                          component="li"
                          {...optionProps}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            py: 1,
                            px: 1.5,
                          }}
                        >
                          <Checkbox checked={isSelected} size="small" sx={{ mr: 1, mt: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {option.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                mt: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {test.purpose}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                    renderGroup={params => (
                      <li key={params.key}>
                        <Box
                          sx={{
                            position: 'sticky',
                            top: '-8px',
                            padding: '12px 16px 8px 16px',
                            backgroundColor:
                              theme.palette.mode === 'light'
                                ? theme.palette.grey[50]
                                : theme.palette.grey[900],
                            zIndex: 1,
                          }}
                        >
                          <Typography
                            variant="overline"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 600,
                              letterSpacing: 1,
                            }}
                          >
                            {params.group}
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ padding: 0 }}>
                          {params.children}
                        </Box>
                      </li>
                    )}
                  />
                );
              }}
            />

            {errors.lab_tests && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.lab_tests.message}
              </Typography>
            )}
          </FormControl>

          {/* ICD-10 Codes */}
          <FormControl fullWidth error={!!errors.icd_10_codes}>
            <Typography variant="subtitle1" gutterBottom>
              ICD-10 Codes *
            </Typography>
            <Controller
              name="icd_10_codes"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  multiple
                  freeSolo
                  disableClearable
                  options={[]}
                  value={value || []}
                  onChange={(event, newValue) => {
                    // Convert all values to uppercase
                    const upperCaseValues = newValue.map(val =>
                      typeof val === 'string' ? val.toUpperCase() : val,
                    );
                    onChange(upperCaseValues);
                  }}
                  renderValue={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          label={option}
                          size="small"
                          color="primary"
                          {...chipProps}
                        />
                      );
                    })
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="Type diagnosis code and press Enter (e.g., E11.9, I10, M79.3)"
                      helperText="Press Enter to add each code. Codes will be automatically capitalized."
                      error={!!errors.icd_10_codes}
                    />
                  )}
                />
              )}
            />
            {errors.icd_10_codes && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.icd_10_codes.message}
              </Typography>
            )}
          </FormControl>

          {/* Special Instructions */}
          <FormControl fullWidth>
            <Typography variant="subtitle1" gutterBottom>
              Special Instructions (Optional)
            </Typography>
            <Controller
              name="special_instructions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  placeholder="(e.g., Fasting required. No food or drink except water for 8-12 hours before test)"
                />
              )}
            />
          </FormControl>

          {/* Digital Signature */}
          <FormControl fullWidth>
            <Controller
              name="sign_with_name"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                      onChange={e => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Digitally sign document?</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your name will appear as a cursive signature on the PDF
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </FormControl>

          {/* Validation Summary */}
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Please fix the following errors:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {errors.fax_number && (
                  <li>
                    <Typography variant="caption">
                      Provider Fax: {errors.fax_number.message}
                    </Typography>
                  </li>
                )}
                {errors.npi_number && (
                  <li>
                    <Typography variant="caption">
                      Provider NPI: {errors.npi_number.message}
                    </Typography>
                  </li>
                )}
                {errors.communications_name && (
                  <li>
                    <Typography variant="caption">
                      Communications Name: {errors.communications_name.message}
                    </Typography>
                  </li>
                )}
                {errors.signing_name && (
                  <li>
                    <Typography variant="caption">
                      Signing Name: {errors.signing_name.message}
                    </Typography>
                  </li>
                )}
                {errors.patient_dob && (
                  <li>
                    <Typography variant="caption">
                      {subjectLanguageCap} Date of Birth: {errors.patient_dob.message}
                    </Typography>
                  </li>
                )}
                {errors.patient_id && (
                  <li>
                    <Typography variant="caption">
                      {subjectLanguageCap}: {errors.patient_id.message}
                    </Typography>
                  </li>
                )}
                {errors.lab_tests && (
                  <li>
                    <Typography variant="caption">Lab Tests: {errors.lab_tests.message}</Typography>
                  </li>
                )}
                {errors.icd_10_codes && (
                  <li>
                    <Typography variant="caption">
                      ICD-10 Codes: {errors.icd_10_codes.message}
                    </Typography>
                  </li>
                )}
              </Box>
            </Alert>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || isSubmitting}
            startIcon={
              isLoading || isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined
            }
            sx={{ mt: 2 }}
          >
            {isLoading || isSubmitting ? 'Processing...' : 'Generate Labs PDF'}
          </Button>
        </Stack>

        {/* Save Bundle Dialog */}
        <SaveBundleDialog
          open={saveBundleDialogOpen}
          onClose={() => setSaveBundleDialogOpen(false)}
          testNames={watchedLabTests || []}
          specialInstructions={watchedSpecialInstructions || ''}
        />
      </Box>
    </EntitlementGate>
  );
};
